<template>
  <transition name="fade">
    <div v-if="isOpen" class="inspector-overlay" @click.self="close">
      <transition name="slide-up" appear>
        <div class="inspector-modal">
          
          <!-- Header -->
          <div class="inspector-header">
            <div class="header-title">
              <!-- Icon from image -->
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M12.6201 4.5H18.8701C19.9801 4.5 20.8701 5.39 20.8701 6.5V11.63C20.8701 12.33 20.4501 12.98 19.8501 13.35L14.0801 16.94C13.2301 17.49 12.0001 17.49 11.1501 16.94L5.38011 13.35C4.78011 12.98 4.36011 12.33 4.36011 11.63V6.5C4.36011 5.39 5.25011 4.5 6.36011 4.5H11.3801" stroke="#F59E0B" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                <path d="M15.3699 10.5H15.3799" stroke="#F59E0B" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                <path d="M10.8701 10.5H10.8801" stroke="#F59E0B" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                <path d="M11.6992 19.5H13.6992" stroke="#F59E0B" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
              <h3>{{ nodes.length }} Role{{ nodes.length > 1 ? 's' : '' }} Selected</h3>
            </div>
            <button class="close-btn" @click="close" aria-label="Close" title="Close">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>
            </button>
          </div>

          <!-- Tabs -->
          <div class="inspector-tabs" v-if="nodes.length > 0">
            <button 
              v-for="node in nodes" 
              :key="node.data.selfId"
              class="tab-btn"
              :class="{ active: activeTabId === node.data.selfId }"
              @click="activeTabId = node.data.selfId"
            >
              {{ node.data.title }}
            </button>
          </div>

          <!-- Body -->
          <div class="inspector-body">
            <div v-if="isLoading" class="loader-container">
              <div class="spinner"></div>
            </div>
            <div v-else-if="activeRoleData" class="role-content">
              <pre class="json-container"><code>{{ activeRoleJson }}</code></pre>
            </div>
             <p v-else class="empty-state">No role data available.</p>
          </div>

          <!-- Footer -->
          <div class="inspector-footer">
            <button class="footer-btn copy-json-btn" @click="copyJson" :class="{ 'copied': isCopied }">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path></svg>
              <span>{{ isCopied ? 'Copied!' : 'Copy JSON' }}</span>
            </button>
            <button class="footer-btn copy-ai-btn" @click="copyAiPrompt" :class="{ 'copied': isAiPromptCopied }">
               <svg width="18" height="18" viewBox="0 0 24 24" fill="#1e293b" xmlns="http://www.w3.org/2000/svg"><path d="M12.3201 2.34985L13.8801 5.48985L17.0201 7.04985L13.8801 8.61985L12.3201 11.7599L10.7601 8.61985L7.62012 7.04985L10.7601 5.48985L12.3201 2.34985Z"/><path d="M4.79004 7.61987L6.35004 10.7599L9.49004 12.3299L6.35004 13.8999L4.79004 17.0399L3.23004 13.8999L0.0900391 12.3299L3.23004 10.7599L4.79004 7.61987Z"/><path d="M18.8899 12.3201L17.3299 15.4601L14.1899 17.0201L17.3299 18.5901L18.8899 21.7301L20.4499 18.5901L23.5899 17.0201L20.4499 15.4601L18.8899 12.3201Z"/></svg>
              <span>{{ isAiPromptCopied ? 'Copied!' : 'Copy AI Prompt' }}</span>
            </button>
          </div>
        </div>
      </transition>
    </div>
  </transition>
</template>

<script>
export default {
  name: 'RoleInspectorPopup',
  data() {
    return {
      isOpen: false,
      isLoading: false,
      nodes: [],
      activeTabId: null,
      fetchedRoleData: {}, 
      originalBodyOverflow: '',
      isCopied: false,
      isAiPromptCopied: false,
    };
  },
  computed: {
    activeRoleData() {
      return this.fetchedRoleData[this.activeTabId] || null;
    },
    allSelectedRolesData() {
        if (!this.nodes.length || Object.keys(this.fetchedRoleData).length === 0) {
            return [];
        }
        return this.nodes
            .map(node => this.fetchedRoleData[node.data.selfId])
            .filter(Boolean);
    },
    activeRoleJson() {
      if (!this.activeRoleData) return '{}';
      return JSON.stringify(this.activeRoleData, null, 2);
    },
    aiPromptText() {
        const rolesData = this.allSelectedRolesData;
        if (rolesData.length === 0) return '';

        const jsonString = JSON.stringify(rolesData, null, 2);
        const roleCount = rolesData.length;
        const roleNoun = roleCount > 1 ? 'roles' : 'role';

        return `You are an organizational design expert. Analyze the following ${roleCount} ${roleNoun} from our organization and provide insights on:

1. Team structure efficiency — are reporting lines optimal?
2. Skill gaps — what capabilities are missing across these roles?
3. Redundancies — any overlapping responsibilities?
4. Hiring priorities — which open roles should be filled first and why?
5. Collaboration opportunities — how should these roles interact?

Here is the role data:

\`\`\`json
${jsonString}
\`\`\`

Provide actionable recommendations.`;
    }
  },
  methods: {
    async copyJson() {
      try {
        await navigator.clipboard.writeText(this.activeRoleJson);
        this.isCopied = true;
        setTimeout(() => {
          this.isCopied = false;
        }, 2000);
      } catch (err) {
        console.error('Failed to copy JSON: ', err);
      }
    },  
    async copyAiPrompt() {
      try {
        await navigator.clipboard.writeText(this.aiPromptText);
        this.isAiPromptCopied = true;
        setTimeout(() => {
          this.isAiPromptCopied = false;
        }, 2000);
      } catch (err) {
        console.error('Failed to copy AI Prompt: ', err);
      }
    },
    async open(nodes) {
      if (!nodes || nodes.length === 0) return;
      
      this.originalBodyOverflow = document.body.style.overflow;
      document.body.style.overflow = 'hidden';

      this.nodes = nodes;
      this.activeTabId = nodes[0].data.selfId;
      this.fetchedRoleData = {};
      this.isLoading = true;
      this.isOpen = true;

      const token = localStorage.getItem('accessToken') || localStorage.getItem('feathers-jwt');
      
      try {
        const promises = nodes.map(node => 
          fetch(`https://api.getboarded.tech/kvdr-role?selfId=${node.data.selfId}`, {
            headers: { 'Authorization': `Bearer ${token}` }
          }).then(r => r.json())
        );

        const results = await Promise.all(promises);
        
        results.forEach((res, i) => {
          if (res.data && res.data[0]) {
            this.$set(this.fetchedRoleData, nodes[i].data.selfId, res.data[0]);
          }
        });
      } catch (e) {
        console.error("Failed to fetch role insights:", e);
      } finally {
        this.isLoading = false;
      }
    },
    close() {
      document.body.style.overflow = this.originalBodyOverflow;
      this.isOpen = false;
      this.nodes = [];
    }
  }
};
</script>

<style scoped>
.inspector-overlay {
  position: fixed;
  top: 0; left: 0; width: 100vw; height: 100vh;
  background: rgba(25, 25, 25, 0.1);
  backdrop-filter: blur(8px);
  z-index: 100000;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: default;
}

.inspector-modal {
  width: 90%;
  max-width: 800px;
  height: 85vh;
  max-height: 800px;
  background: #fdfdfc;
  border-radius: 16px;
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.15);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  font-family: 'Inter', sans-serif;
  border: 1px solid #f0efed;
}

/* Header */
.inspector-header {
  padding: 16px 24px;
  border-bottom: 1px solid #f0efed;
  display: flex; justify-content: space-between; align-items: center;
  flex-shrink: 0;
}
.header-title {
  display: flex;
  align-items: center;
  gap: 12px;
}
.inspector-header h3 {
  margin: 0; font-size: 1rem; font-weight: 600; color: #1e293b;
}
.close-btn {
  background: transparent; border: none; width: 32px; height: 32px;
  border-radius: 50%; display: flex; align-items: center; justify-content: center;
  cursor: pointer; color: #94a3b8; transition: all 0.2s ease;
}
.close-btn:hover { background: #f1f5f9; color: #334155; }

/* Tabs */
.inspector-tabs {
  display: flex; padding: 12px 24px;
  border-bottom: 1px solid #f0efed; gap: 8px; overflow-x: auto;
  flex-shrink: 0;
  background-color: #ffffff;
}
.tab-btn {
  background: #f1f5f9;
  border: 1px solid #e2e8f0;
  padding: 6px 14px;
  border-radius: 20px;
  font-size: 0.875rem; 
  font-weight: 500; 
  color: #475569;
  cursor: pointer; 
  white-space: nowrap; 
  transition: all 0.2s;
  outline: none;
}
.tab-btn:hover {
  background-color: #feefc7;
  border-color: #fde6a8;
  color: #92400e;
}
.tab-btn.active {
  background-color: #fef3c7;
  border-color: #fde6a8;
  color: #92400e;
  font-weight: 600;
}

/* Body */
.inspector-body {
  flex: 1; overflow: hidden;
  padding: 0;
  position: relative;
}
.loader-container {
  height: 100%; display: flex; align-items: center; justify-content: center;
}
.spinner {
  width: 32px; height: 32px; border: 3px solid #f1f5f9;
  border-top-color: #F59E0B; border-radius: 50%;
  animation: spin 1s linear infinite;
}
@keyframes spin { to { transform: rotate(360deg); } }

.role-content {
  height: 100%;
  overflow: hidden;
}

.json-container {
  margin: 0;
  padding: 24px;
  height: 100%;
  box-sizing: border-box;
  overflow-y: auto;
  font-family: 'Fira Code', 'Courier New', monospace;
  font-size: 13px; line-height: 1.6; color: #334155;
  white-space: pre-wrap;
  word-break: break-word;
  background-color: transparent;
}
.json-container::-webkit-scrollbar {
  width: 12px;
}
.json-container::-webkit-scrollbar-track {
  background: transparent;
}
.json-container::-webkit-scrollbar-thumb {
  background-color: #e2e8f0;
  border-radius: 20px;
  border: 3px solid #fdfdfc;
}
.json-container::-webkit-scrollbar-thumb:hover {
  background-color: #cbd5e1;
}

/* Footer */
.inspector-footer {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  padding: 16px 24px;
  border-top: 1px solid #f0efed;
  background-color: #ffffff;
  flex-shrink: 0;
}
.footer-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  border-radius: 8px;
  font-size: 0.875rem;
  font-weight: 600;
  padding: 10px 16px;
  cursor: pointer;
  border: 1px solid transparent;
  transition: all 0.2s ease;
  outline: none;
}

.copy-json-btn {
  background-color: #ffffff;
  color: #334155;
  border-color: #e2e8f0;
}
.copy-json-btn:hover {
  background-color: #f8fafc;
  border-color: #d1d5db;
}
.copy-json-btn.copied {
  border-color: #10b981;
  color: #10b981;
}

.copy-ai-btn {
  background-color: #F59E0B;
  color: #1e293b;
  border-color: #F59E0B;
}
.copy-ai-btn:hover {
  background-color: #fbbf24;
  border-color: #fbbf24;
}
.copy-ai-btn.copied {
  background-color: #10b981;
  border-color: #10b981;
  color: white;
}
/* Vue Animations */
.fade-enter-active, .fade-leave-active { transition: opacity 0.3s ease; }
.fade-enter, .fade-leave-to { opacity: 0; }
.slide-up-enter-active, .slide-up-leave-active { transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1); }
.slide-up-enter, .slide-up-leave-to { opacity: 0; transform: translateY(20px) scale(0.98); }
.empty-state { margin: 24px; font-size: 0.85rem; color: #94a3b8; }
</style>